import { useMemo } from 'react';
import { FundingPolicyType } from '@shared/appModel';
import { getPresentation } from '../projectionTable/strategies/registry';
import { buildDefaultAccumulationColumns } from './columns';
import { AccumulationColumnSpec } from './types';

interface UseAccumulationTimelineResult {
  timeline: AccumulationYear[];
  columns: AccumulationColumnSpec[];
}

/**
 * Builds the column set + rows for the pre-care Accumulation Timeline table.
 * Rows come straight from the engine (`projection.accumulationTimeline`); the
 * columns come from the strategy presentation override or the universal
 * default. Premiums are NOT shown here — the dedicated "Policy cost &
 * premiums" section owns the premium cashflow schedule. Returns an empty
 * timeline when there is no accumulation phase (care begins in policy year 1,
 * or a pre-policy claim) so the section can hide.
 */
export function useAccumulationTimeline(
  projection: ClaimProjection,
  policyType: FundingPolicyType | null | undefined,
): UseAccumulationTimelineResult {
  const timeline = projection.accumulationTimeline ?? [];
  const presentation = useMemo(() => getPresentation(policyType), [policyType]);

  const columns = useMemo(() => {
    if (timeline.length === 0) return [];
    const ctx = { terms: projection.terms, timeline };
    return presentation.accumulationColumns?.(ctx) ?? buildDefaultAccumulationColumns(ctx);
  }, [projection.terms, timeline, presentation]);

  return { timeline, columns };
}
